import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaBoxOpen, FaSlidersH } from 'react-icons/fa';
import { toast } from 'react-toastify';

import {
   createMovement,
   readMovementApiError,
} from '../../services/movements/movementsApi';
import { notifyStockWarnings } from '../../utils/movementFeedback';
import Loading from '../../components/Loading';
import DuplicateMovementsModal, {
   DuplicateCheckHint,
} from '../../components/DuplicateMovementsModal';
import StockImpactModal, {
   StockImpactHint,
} from '../../components/StockImpactModal';
import MovementConfirmModal from '../../components/MovementConfirmModal';
import MovementItemsEditor from '../../components/movements/MovementItemsEditor';
import MovementSummaryAside from '../../components/movements/MovementSummaryAside';
import {
   DOC_TYPE_OPTIONS_OUT,
   INTEGER_RE,
   sumItemUnits,
} from '../../components/movements/constants';
import { useMovementDuplicateCheck } from '../../hooks/useMovementDuplicateCheck';
import { useStockImpactCheck } from '../../hooks/useStockImpactCheck';
import { useUserPdvs } from '../../hooks/useUserPdvs';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   ComposerPageGrid,
   Field,
   FieldInput,
   FieldLabel,
   FieldSelect,
   FieldTextarea,
   FormCard,
   FormCardTop,
   FormGrid,
   Section,
   SectionHead,
   SectionHeadRight,
   SectionIcon,
   SectionTitles,
} from '../../styles/ui/Composer';
import { GhostOnDarkButton } from '../../styles/ui/Button';

// ===========================================================================
// Saída de mercadorias (`POST /movements`).
//
// Gêmea da entrada: mesmo composer (FC7 cap.2 §H.3), mesmos warn guards e
// mesmo editor de itens compartilhado. As diferenças de domínio são o
// `move_type: 'out'`, a lista de tipos de documento e o peso do aviso de saldo
// negativo — numa saída ele é o caso comum, e segue NÃO bloqueante (0093 §5a).
//
// Também aqui não existe seletor Entrada|Saída (decisão do Caio, 2026-09-12):
// o tipo é definido pela rota / entrada de menu.
// ===========================================================================

export default function MovementOut() {
   const userId = useSelector((state) => state.auth.user.id);
   const navigate = useNavigate();
   const location = useLocation();

   const { pdvs, isLoading: loadingPdvs } = useUserPdvs();

   // params
   const [pdvId, setPdvId] = useState('');
   const [docType, setDocType] = useState('');
   const [docNumber, setDocNumber] = useState('');
   const [description, setDescription] = useState('');

   const [productList, setProductList] = useState([]);
   const [paramList, setParamList] = useState({});

   const [isLoading, setIsLoading] = useState(false);
   // Remonta o editor de itens ao limpar (o estado da linha de adição é dele).
   const [itemsEditorKey, setItemsEditorKey] = useState(0);

   // Modal unificado de confirmação do Salvar: null = fechado; aberto guarda
   // os achados FRESCOS dos dois warn guards.
   const [confirmState, setConfirmState] = useState(null);

   // Warn guard de duplicidade (handoff 0089) — informativo, nunca bloqueia.
   const duplicateCheck = useMovementDuplicateCheck({
      pdvId,
      docType,
      docNumber,
      moveType: 'out',
   });

   // Gatilho "cedo" na troca dos selects: só quando o conjunto já está
   // completo. O nº documento dispara no blur (não a cada tecla).
   useEffect(() => {
      if (pdvId && docType && docNumber) duplicateCheck.triggerCheck();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [pdvId, docType]);

   // Warn guard de saldo negativo (handoff 0093) — informativo, nunca bloqueia.
   const stockImpact = useStockImpactCheck({
      pdvId,
      moveType: 'out',
      items: productList,
   });

   // Gatilho "cedo" do impacto: re-checa quando a lista de itens/PDV muda
   // (ações explícitas — o hook cacheia e deduplica; alimenta só o indicador).
   useEffect(() => {
      stockImpact.triggerCheck();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [productList, pdvId]);

   useEffect(() => {
      setParamList({
         pdv_id: pdvId,
         move_type: 'out',
         doc_type: docType,
         doc_number: docNumber,
         movement_description: description,
         user_id: userId,
      });
   }, [docNumber, docType, pdvId, description, userId]);

   const clearFields = () => {
      setPdvId('');
      setDocType('');
      setDocNumber('');
      setDescription('');
      setProductList([]);
      setParamList({});

      duplicateCheck.reset();
      stockImpact.reset();
      setConfirmState(null);
      setItemsEditorKey((key) => key + 1);
   };

   const handleClearFields = () => {
      // eslint-disable-next-line no-alert
      const ok = window.confirm('Limpar todos os campos e itens desta saída?');
      if (!ok) return;
      clearFields();
   };

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/movements');
   };

   const handleSubmitEntry = async () => {
      if (
         !paramList.pdv_id ||
         !paramList.move_type ||
         !paramList.doc_type ||
         !paramList.doc_number ||
         !paramList.user_id ||
         productList.length === 0
      ) {
         toast.error('Campos obrigatórios vazios.');
         return;
      }

      try {
         setIsLoading(true);

         // JSend Geração 3 (handoff 0093): sucesso desembrulhado no service.
         const { movement, stockWarnings, message } = await createMovement({
            movement: paramList,
            items: productList,
         });

         toast.success(message);
         notifyStockWarnings(stockWarnings);

         if (movement?.id) {
            navigate(`/movements/${movement.id}`);
         } else {
            toast.warn('Movimento salvo, mas não foi possível obter o ID.');
         }
      } catch (err) {
         const apiError = readMovementApiError(err);
         Object.entries(apiError.fieldErrors).forEach(([field, msg]) =>
            toast.error(`${field}: ${msg}`)
         );
         if (!Object.keys(apiError.fieldErrors).length) {
            toast.error(apiError.message);
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Guarda do Salvar: consulta os dois warn guards FRESCOS (em paralelo) e
   // abre o modal unificado de confirmação — que substitui o window.confirm
   // e exibe os achados sem nunca bloquear.
   const handleConfirmRegister = async () => {
      const [duplicates, negativeStock] = await Promise.all([
         duplicateCheck.checkNow(),
         stockImpact.checkNow(),
      ]);
      setConfirmState({ duplicates, negativeStock });
   };

   const handleConfirmModalConfirm = () => {
      setConfirmState(null);
      handleSubmitEntry();
   };

   // Só é verdade no instante em que o campo está vazio COM O FOCO DENTRO: o
   // stepper normaliza para 1 no blur. Rede de segurança contra a corrida
   // "apaga a quantidade e clica em Salvar" (`Number('')` seria 0).
   const hasInvalidQty = productList.some(
      (item) =>
         String(item.prod_qtd ?? '').trim() === '' || Number(item.prod_qtd) < 1
   );

   const hasParams = !!pdvId && !!docType && !!docNumber;
   const canSave = hasParams && productList.length > 0 && !hasInvalidQty;

   const pdvName = pdvs.find((pdv) => String(pdv.id) === String(pdvId))
      ?.pdv_name;
   const totalUnits = sumItemUnits(productList);

   return (
      <Container>
         <Loading isLoading={isLoading || loadingPdvs} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Nova saída</PageTitle>
               <PageSubtitle>
                  Registre a saída de produtos do estoque de um PDV.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <GhostOnDarkButton type="button" onClick={handleBack}>
                  ← Voltar
               </GhostOnDarkButton>
            </PageHeaderActions>
         </PageHeader>

         <ComposerPageGrid>
            <FormCard>
               <FormCardTop>
                  <h2>Saída de mercadorias</h2>
                  <p>
                     Informe o PDV e o documento de saída, monte a lista de
                     itens e confirme — o saldo do PDV é baixado no registro.
                  </p>
               </FormCardTop>

               <Section>
                  <SectionHead>
                     <SectionIcon>
                        <FaSlidersH size={15} aria-hidden="true" />
                        <span className="step">1</span>
                     </SectionIcon>
                     <SectionTitles>
                        <div className="t">Parâmetros</div>
                        <div className="d">
                           Define de onde e por qual documento o estoque será
                           baixado.
                        </div>
                     </SectionTitles>
                  </SectionHead>

                  <FormGrid>
                     <Field $span={4}>
                        <FieldLabel htmlFor="movement-out-pdv">
                           PDV<span className="req">*</span>
                        </FieldLabel>
                        <FieldSelect
                           id="movement-out-pdv"
                           value={pdvId}
                           onChange={(event) => setPdvId(event.target.value)}
                           disabled={isLoading || loadingPdvs}
                        >
                           <option value="">Selecione um PDV</option>
                           {pdvs.map((pdv) => (
                              <option key={pdv.id} value={pdv.id}>
                                 {pdv.pdv_name}
                              </option>
                           ))}
                        </FieldSelect>
                     </Field>

                     <Field $span={4}>
                        <FieldLabel htmlFor="movement-out-doc-type">
                           Documento de saída<span className="req">*</span>
                        </FieldLabel>
                        <FieldSelect
                           id="movement-out-doc-type"
                           value={docType}
                           onChange={(event) => setDocType(event.target.value)}
                           disabled={isLoading}
                        >
                           <option value="" disabled>
                              Selecione o tipo de documento
                           </option>
                           {DOC_TYPE_OPTIONS_OUT.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </FieldSelect>
                     </Field>

                     <Field $span={4}>
                        <FieldLabel htmlFor="movement-out-doc-number">
                           Nº do documento<span className="req">*</span>
                        </FieldLabel>
                        <FieldInput
                           id="movement-out-doc-number"
                           type="text"
                           inputMode="numeric"
                           value={docNumber}
                           disabled={isLoading}
                           placeholder="Ex.: 424242"
                           onChange={(event) => {
                              const { value } = event.target;
                              if (value === '' || INTEGER_RE.test(value)) {
                                 setDocNumber(value);
                              }
                           }}
                           onBlur={duplicateCheck.triggerCheck}
                        />
                        <DuplicateCheckHint
                           status={duplicateCheck.status}
                           count={duplicateCheck.duplicates.length}
                           onOpen={duplicateCheck.reopenModal}
                        />
                     </Field>

                     <Field $span={12}>
                        <FieldLabel htmlFor="movement-out-description">
                           Descrição
                        </FieldLabel>
                        <FieldTextarea
                           id="movement-out-description"
                           value={description}
                           disabled={isLoading}
                           onChange={(event) =>
                              setDescription(event.target.value.toUpperCase())
                           }
                           placeholder="Ex.: Saída para entrega técnica / ajuste / transferência..."
                        />
                     </Field>
                  </FormGrid>
               </Section>

               <Section>
                  <SectionHead>
                     <SectionIcon>
                        <FaBoxOpen size={15} aria-hidden="true" />
                        <span className="step">2</span>
                     </SectionIcon>
                     <SectionTitles>
                        <div className="t">Itens</div>
                        <div className="d">
                           {productList.length
                              ? `${productList.length} ${
                                   productList.length === 1 ? 'item' : 'itens'
                                } · ${totalUnits} un.`
                              : 'Bipe o código ou busque pelo nome do produto.'}
                        </div>
                     </SectionTitles>
                     <SectionHeadRight>
                        <StockImpactHint
                           status={stockImpact.status}
                           count={stockImpact.negativeStock.length}
                           onOpen={stockImpact.reopenModal}
                           onRefresh={stockImpact.triggerCheck}
                        />
                     </SectionHeadRight>
                  </SectionHead>

                  <MovementItemsEditor
                     key={itemsEditorKey}
                     items={productList}
                     onChange={setProductList}
                     disabled={isLoading}
                     pdvId={pdvId}
                     emptyDescription="Selecione o PDV, bipe o SKU (ou busque pelo nome) e informe a quantidade."
                  />
               </Section>
            </FormCard>

            <MovementSummaryAside
               typeLabel="Saída"
               typeVariant="blue"
               rows={[
                  { label: 'Tipo', value: 'Saída de mercadorias' },
                  { label: 'PDV', value: pdvName },
                  {
                     label: 'Documento',
                     value: [docType, docNumber && `nº ${docNumber}`]
                        .filter(Boolean)
                        .join(' '),
                  },
                  { label: 'Itens', value: productList.length || '0' },
                  { label: 'Quantidade total', value: `${totalUnits} un.` },
               ]}
               /* Quantidade NÃO é item de checklist (2026-09-12): com a lista
                  vazia ele ficava verde por vacuidade — "preenchidas" sem nada
                  para preencher. O stepper normaliza o campo no blur, então
                  item na lista sempre tem quantidade válida; o guard abaixo é
                  só rede de segurança do instante em que o campo está vazio
                  com o foco dentro. */
               checklist={[
                  { label: 'PDV e documento informados', ok: hasParams },
                  {
                     label: 'Pelo menos um item na lista',
                     ok: productList.length > 0,
                  },
               ]}
               primaryLabel="Salvar saída"
               onPrimary={handleConfirmRegister}
               primaryDisabled={isLoading || !canSave}
               primaryTitle={
                  hasInvalidQty
                     ? 'Há item com a quantidade em branco — conclua a edição para salvar'
                     : undefined
               }
               secondaryLabel="Limpar"
               onSecondary={handleClearFields}
               secondaryDisabled={isLoading}
               note="Saldo negativo é permitido e gera apenas aviso na confirmação — a operação decide."
            />
         </ComposerPageGrid>

         <DuplicateMovementsModal
            isOpen={duplicateCheck.isModalOpen}
            duplicates={duplicateCheck.duplicates}
            queryInfo={{ pdvName, docType, docNumber }}
            onClose={duplicateCheck.closeModal}
         />

         <StockImpactModal
            isOpen={stockImpact.isModalOpen}
            negativeStock={stockImpact.negativeStock}
            queryInfo={{ pdvName, moveTypeLabel: 'Saída' }}
            onClose={stockImpact.closeModal}
         />

         <MovementConfirmModal
            isOpen={!!confirmState}
            subtitle={[
               pdvName,
               'Saída',
               docType,
               docNumber ? `nº ${docNumber}` : '',
            ]
               .filter(Boolean)
               .join(' · ')}
            itemsCount={productList.length}
            duplicates={confirmState?.duplicates ?? []}
            negativeStock={confirmState?.negativeStock ?? []}
            onCancel={() => setConfirmState(null)}
            onConfirm={handleConfirmModalConfirm}
         />
      </Container>
   );
}
